import React, { useState, useRef, useEffect } from "react";
import moment from "moment-timezone";
import Divider from "@mui/material/Divider";
import PropTypes from "prop-types";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { ContainedButton, OutlinedButton } from "common/DS/Buttons";
import ConversationBubble from "common/DS/ConversationBubble";
import DSList from "common/DS/List";
import ThreeLineListItem from "common/DS/ThreeLineListItem";
import Loader from "common/Loader";
import { ANSWER_GROUP_REPLY_TYPE } from "lib/constants";
import { useErrorToast, useDefaultToast } from "lib/hooks/toasts";
import { TranslationFn } from "lib/types";
import Input from "portal/Retain/AnswerReply/RetainAnswerReplyInput";
import { usePost } from "redux/fetch-hooks";
import { Reaction } from "workstep/pages/Feedback/Comments/state/types";
import { newlineToHtml } from "workstep/util/formatting";
import { Caption, H6 } from "common/DS/Text";
import { feature } from "lib/featureFlags";
import HubNote from "employee/HubNote";
import ProfileIconButton from "employee/ProfileIconButton";
import FeatureFlag from "workstep/components/layout/FeatureFlag";

import ConfidentialityLock from "./ConfidentialityLock";
import CandidatePageFooter from "./CandidatePageFooter";

const handleSide = (type: number) => (type === ANSWER_GROUP_REPLY_TYPE.EMPLOYEE ? "right" : "left");

type ReplyOutput = {
    id: number;
    time_created: string;
    time_modified?: string;
    user: number;
    user_name: string;
    company: number;
    answer_group: number;
    content: string;
    reply_type: number;
    reaction?: Reaction;
    translated_content: string;
    translated_from: string | null;
};

const groupRepliesByDate = (
    replies: ReplyOutput[],
): [string[], { [date: string]: ReplyOutput[] }] => {
    const sortedReplies = [...replies].sort((a, b) =>
        moment(a.time_created).diff(moment(b.time_created)),
    );
    const dates = sortedReplies.reduce<{ [date: string]: ReplyOutput[] }>((acc, reply) => {
        const date = moment(reply.time_created).calendar(null, {
            sameDay: "[Today]",
            lastDay: "[Yesterday]",
            lastWeek: "ddd, MMM Do",
            sameElse: "ddd, MMM Do",
        });
        if (!acc[date]) {
            acc[date] = [];
        }
        acc[date].push(reply);
        return acc;
    }, {});
    const dateNames = Object.keys(dates).sort((a, b) =>
        moment(dates[a][0].time_created).diff(moment(dates[b][0].time_created)),
    );
    return [dateNames, dates];
};

const TranslationToggle = (
    {
        translatedFrom,
        showTranslation,
        setShowTranslation,
    }: {
        translatedFrom: string | null;
        showTranslation: boolean;
        setShowTranslation: (showTranslation: boolean) => void;
    },
    { t: _ }: { t: TranslationFn },
) => {
    if (!translatedFrom) {
        return null;
    }
    return (
        <div className="employee-translation-toggle">
            {!showTranslation && (
                <Caption>
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            setShowTranslation(true);
                        }}
                    >
                        {_("Translate")}
                    </a>
                </Caption>
            )}
            {showTranslation && (
                <Caption className="color-navy-medium flex flex-row gap-sm">
                    <span>{_(`Translated from ${translatedFrom}`)}</span>
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            setShowTranslation(false);
                        }}
                    >
                        {_(`Show original`)}
                    </a>
                </Caption>
            )}
        </div>
    );
};

TranslationToggle.contextTypes = {
    t: PropTypes.func,
};

const ReplyBubble = (
    {
        reply,
        handleReactionChange,
        reactionLoading = false,
    }: {
        reply: ReplyOutput;
        handleReactionChange?: ((reaction: Maybe<Reaction>) => void) | undefined;
        reactionLoading?: boolean;
    },
    { t: _ }: { t: TranslationFn },
) => {
    const [showTranslation, setShowTranslation] = useState(false);
    let content = <div>{reply.content}</div>;
    if (reply.reply_type === ANSWER_GROUP_REPLY_TYPE.HIRING_MANAGER) {
        const translatedContent = showTranslation ? reply.translated_content : reply.content;
        content = (
            // eslint-disable-next-line react/no-danger
            <div dangerouslySetInnerHTML={{ __html: newlineToHtml(translatedContent) }} />
        );
    }

    return (
        <>
            <ConversationBubble
                from={
                    (reply.reply_type === ANSWER_GROUP_REPLY_TYPE.HIRING_MANAGER &&
                        reply.user_name) ||
                    ""
                }
                time={moment(reply.time_created).format("LT")}
                side={handleSide(reply.reply_type)}
                message={content}
                reaction={reply.reaction}
                onReactionChange={handleReactionChange}
                reactionLoading={reactionLoading}
            />
            <TranslationToggle
                translatedFrom={reply.translated_from}
                showTranslation={showTranslation}
                setShowTranslation={setShowTranslation}
            />
        </>
    );
};

ReplyBubble.contextTypes = {
    t: PropTypes.func,
};

type Props = {
    answers: {
        question: string;
        comment: string;
        value: string;
        anonymous: boolean;
    }[];
    replies: ReplyOutput[];
    closed: boolean;
    enableReactions: boolean;
    showToast: boolean;
    companyName: string;
};

export default function EmployeeConversation(
    { answers, replies, closed, enableReactions, showToast, companyName }: Props,
    { t: _ }: { t: TranslationFn },
) {
    const errorToast = useErrorToast();
    const defaultToast = useDefaultToast();
    const [repliesState, setRepliesState] = useState(replies);
    const [showInput, setShowInput] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const isAnonymous = answers.every((x) => x.anonymous);
    const isHubTwoPointO = feature("hub-2-0") === true;
    const chatThreadRef = useRef<HTMLDivElement | null>(null);
    const onCloseInput = () => {
        setInputValue("");
        setShowInput(false);
    };

    const [saveReply, { isLoading: repliesLoading }] = usePost<{ reply: ReplyOutput }>(
        window.location.pathname,
        {
            onSuccess: (data) => {
                setRepliesState((prevState) => [...prevState, data.reply]);
                onCloseInput();
            },
        },
    );
    const [setReaction, { isLoading: reactionLoading }] = usePost<{ reply: ReplyOutput }>(
        window.location.pathname,
        {
            onSuccess: (data) => {
                setRepliesState((prevState) =>
                    prevState.map((r) => (r.id === data.reply.id ? data.reply : r)),
                );
            },
            onError: () => {
                errorToast("reaction not saved, please try again");
            },
        },
    );
    const handleReactionChange = (replyId: number, value: Maybe<Reaction>) => {
        setReaction({ data: { reaction_type: value?.type || null, reply_id: replyId } });
    };
    useEffect(() => {
        // For some reason, without deferring scroll to at least the next event
        // cycle, the scroll sometimes doesn't bring the target ref entirely
        // into view.
        setTimeout(() => {
            chatThreadRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
        });
    }, [repliesState]);
    useEffect(() => {
        if (showToast) {
            defaultToast(_("Your reaction has been saved"));
        }
    }, []);

    const onShowInput = () => setShowInput(true);

    const handleSaveReply = () => {
        setShowInput(false);
        saveReply({
            data: { message: inputValue },
        });
    };

    const handleInputChange = (value: string) => {
        setInputValue(value);
    };

    const [dateNames, dates] = groupRepliesByDate(repliesState);

    const isSendDisabled = !showInput || inputValue.length < 1;

    // can only handle a reaction for hiring manager replies, and only if enabled
    const canReact = (type: number) =>
        enableReactions && type === ANSWER_GROUP_REPLY_TYPE.HIRING_MANAGER;

    return (
        <div className="retain-conversation">
            <div className="retain-conversation__container">
                <FeatureFlag featureName="hub-2-0">
                    <HubNote />
                </FeatureFlag>
                {isHubTwoPointO && answers[0]?.question && (
                    <a className="retain-conversation__hub-back" href="/hub">
                        <ChevronLeftIcon className="color-navy-high" />
                        <H6 className="color-navy-high">
                            {_("Conversation with {companyName}", { companyName })}
                        </H6>
                    </a>
                )}
                <header className="retain-conversation__header">
                    <ConfidentialityLock isAnonymous={isAnonymous} />
                </header>
                <ProfileIconButton />

                <div className="retain-conversation__card">
                    <div className="retain-conversation__questions">
                        <DSList>
                            {answers.map((a, i) => (
                                <ThreeLineListItem
                                    key={i}
                                    content={a.question}
                                    secondary={a.comment}
                                    tags={a.value ? [{ label: a.value }] : []}
                                />
                            ))}
                        </DSList>
                    </div>
                    <Divider />
                    {repliesLoading && <Loader />}
                    {!repliesLoading &&
                        dateNames.map((date) => (
                            <div key={date}>
                                <span className="retain-conversation__day-replied__text">
                                    {date}
                                </span>

                                <div className="retain-conversation__replies flex flex-col">
                                    {dates[date].map((r) => (
                                        <ReplyBubble
                                            reply={r}
                                            key={r.id}
                                            handleReactionChange={
                                                canReact(r.reply_type)
                                                    ? (reaction: Maybe<Reaction>) =>
                                                          handleReactionChange(r.id, reaction)
                                                    : undefined
                                            }
                                            reactionLoading={reactionLoading}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}

                    {closed && (
                        <span className="flex flex-ac flex-jc text-caption color-navy-low p-md">
                            {_(`${replies[0].user_name} closed the conversation`)}
                        </span>
                    )}
                    {!closed && (
                        <div className="retain-conversation__input__container">
                            <div className="retain-conversation__reply-input">
                                <Input
                                    className="retain-converation__input"
                                    label={_("Reply confidentially")}
                                    multiline={showInput}
                                    autoFocus
                                    minRows={3}
                                    onClick={onShowInput}
                                    handleChange={handleInputChange}
                                    showCounter={showInput}
                                    value={inputValue}
                                />
                                {showInput && (
                                    <footer className="retain-conversation-dialog-footer">
                                        <OutlinedButton
                                            className="heap-retain-conversation-reply-cancel mr-sm"
                                            onClick={onCloseInput}
                                        >
                                            {_("Cancel")}
                                        </OutlinedButton>
                                        <ContainedButton
                                            disabled={isSendDisabled}
                                            onClick={handleSaveReply}
                                        >
                                            {_("Send")}
                                        </ContainedButton>
                                    </footer>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                <div ref={chatThreadRef} style={{ width: "16px", height: "16px" }} />
                <CandidatePageFooter />
            </div>
        </div>
    );
}

EmployeeConversation.contextTypes = {
    t: PropTypes.func,
};
